import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Grid, Paper, IconButton, Alert, FormControlLabel, Checkbox, Slider } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';

interface PasswordOptions {
  length: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
}

export default function PasswordGenerator() {
  const [password, setPassword] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [options, setOptions] = useState<PasswordOptions>({
    length: 12,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: false
  });

  const generatePassword = (opts: PasswordOptions): string => {
    let charset = '';
    
    if (opts.includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (opts.includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (opts.includeNumbers) charset += '0123456789';
    if (opts.includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (charset === '') return '';

    let result = '';
    for (let i = 0; i < opts.length; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  };

  const handleGenerate = () => {
    const newPassword = generatePassword(options);
    setPassword(newPassword);
    setCopySuccess(false);
  };

  const handleCopy = async () => {
    if (password) {
      try {
        await navigator.clipboard.writeText(password);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
  };

  const handleClear = () => {
    setPassword('');
    setCopySuccess(false);
  };

  const handleOptionChange = (key: keyof PasswordOptions, value: boolean | number) => {
    setOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };
  // Generate password when options change
  useEffect(() => {
    if (options.includeUppercase || options.includeLowercase || options.includeNumbers || options.includeSymbols) {
      const newPassword = generatePassword(options);
      setPassword(newPassword);
    }
  }, [options]);

  const hasValidOptions = options.includeUppercase || options.includeLowercase || options.includeNumbers || options.includeSymbols;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Password Generator</Typography>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Generate secure passwords with customizable options for enhanced security.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Password Length: {options.length}
            </Typography>
            <Slider
              value={options.length}
              onChange={(_, value) => handleOptionChange('length', value as number)}
              min={4}
              max={128}
              step={1}
              marks={[
                { value: 4, label: '4' },
                { value: 8, label: '8' },
                { value: 12, label: '12' },
                { value: 16, label: '16' },
                { value: 32, label: '32' },
                { value: 64, label: '64' },
                { value: 128, label: '128' }
              ]}
              sx={{ mb: 2 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Character Types:
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.includeUppercase}
                      onChange={(e) => handleOptionChange('includeUppercase', e.target.checked)}
                    />
                  }
                  label="Uppercase Letters (A-Z)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.includeLowercase}
                      onChange={(e) => handleOptionChange('includeLowercase', e.target.checked)}
                    />
                  }
                  label="Lowercase Letters (a-z)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.includeNumbers}
                      onChange={(e) => handleOptionChange('includeNumbers', e.target.checked)}
                    />
                  }
                  label="Numbers (0-9)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={options.includeSymbols}
                      onChange={(e) => handleOptionChange('includeSymbols', e.target.checked)}
                    />
                  }
                  label="Special Characters (!@#$%^&*)"  
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Generated Password"
              value={password}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={handleCopy}
                    disabled={!password}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                )
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleGenerate}
                startIcon={<RefreshIcon />}
                disabled={!hasValidOptions}
              >
                Generate New Password
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
              >
                Clear
              </Button>
            </Box>
          </Grid>

          {!hasValidOptions && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Please select at least one character type to generate a password.
              </Alert>
            </Grid>
          )}

          {copySuccess && (
            <Grid item xs={12}>
              <Alert severity="success">
                Password copied to clipboard!
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Password Security Tips
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Use at least 12 characters for better security<br/>
                • Include a mix of uppercase, lowercase, numbers, and symbols<br/>
                • Avoid using personal information or common words<br/>
                • Use unique passwords for different accounts<br/>
                • Consider using a password manager
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
